import React, { useState, useEffect, useRef } from 'react';
import { TransformWrapper, TransformComponent, useControls } from 'react-zoom-pan-pinch';
import { toPng } from 'html-to-image';
import './Team.css'; // Your new CSS file
import { MdAccountTree } from 'react-icons/md';
import { IoAdd, IoRemove, IoRefresh, IoDocumentTextOutline } from 'react-icons/io5';
import { FaAngleUp, FaAngleDown } from 'react-icons/fa';
 
// --- Environment Variables ---
const HIERARCHY_API_URL = `${process.env.REACT_APP_API_BASE_URL}/api-get-full-hierarchy-tree`;
const EXPORT_PDF_API_URL = `${process.env.REACT_APP_EXPORT_API_BASE_URL}/export`;
const PROFILE_IMAGE_API_URL = `${process.env.REACT_APP_API_BASE_URL}/api/get-profile-image`;
 
 
// --- API Helpers ---
const getAuthToken = () => localStorage.getItem('zTaskAuthToken');
 
const fetchTreeDataAPI = async () => {
    const token = getAuthToken();
    if (!token) {
        throw new Error("Authentication token not found. Please log in again.");
    }
    const headers = { 'Authorization': `Bearer ${token}`, 'accept': 'application/json' };
   
    const response = await fetch(HIERARCHY_API_URL, { headers });
 
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch hierarchy: ${response.status} - ${errorText || response.statusText}`);
    }
    const data = await response.json();
    return data.tree || [];
};
 
const fetchProfileImageAPI = async (userId) => {
    const token = getAuthToken();
    if (!token || !userId) return null;
    const headers = { 'Authorization': `Bearer ${token}`, 'accept': 'application/json' };
 
    try {
        const response = await fetch(`${PROFILE_IMAGE_API_URL}?userId=${userId}`, { headers });
        if (!response.ok) {
            console.error(`Failed to fetch profile image for userId ${userId}: ${response.status}`);
            return null;
        }
        const data = await response.json();
        if (data && data.message && data.message.image) {
            return `data:image/jpeg;base64,${data.message.image}`;
        }
        return null;
    } catch (error) {
        console.error(`Error fetching profile image for userId ${userId}:`, error);
        return null;
    }
};
 
const exportToPdfAPI = async (base64Image) => {
    const token = getAuthToken();
    if (!token) throw new Error("Authentication token required for export.");
    const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    };
    const payload = {
        body: JSON.stringify({ image: base64Image })
    };
 
    const response = await fetch(EXPORT_PDF_API_URL, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(payload)
    });
 
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`PDF export failed: ${response.status} - ${errorText || response.statusText}`);
    }
    return response.json();
};
 
// --- SKELETON to match new detailed CSS ---
const TeamSkeleton = () => (
    <div className="team-tree-wrapper">
        <ul className="team-tree">
            {Array.from({ length: 3 }).map((_, i) => (
                <li key={i} className="tree-node">
                    <div className="node-card">
                        <div className="shimmer-icon shimmer"></div>
                        <div className="shimmer-details">
                            <div className="shimmer-line shimmer-title shimmer"></div>
                            <div className="shimmer-line shimmer-subtitle shimmer"></div>
                            <div className="shimmer-line shimmer-role shimmer"></div>
                        </div>
                    </div>
                </li>
            ))}
        </ul>
    </div>
);
 
// --- Controls for Zoom/Pan ---
const ZoomControls = () => {
    const { zoomIn, zoomOut, resetTransform } = useControls();
    return (
        <div className="zoom-controls">
            <button onClick={() => zoomIn()} title="Zoom In"><IoAdd /></button>
            <button onClick={() => zoomOut()} title="Zoom Out"><IoRemove /></button>
            <button onClick={() => resetTransform()} title="Reset View"><IoRefresh /></button>
        </div>
    );
};
 
// --- TreeNode Component (Recursive) ---
// UPDATED: TreeNode now shows active/inactive status
const TreeNode = ({ node, depth = 0 }) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const [profileImage, setProfileImage] = useState(null);
    const [isLoadingImage, setIsLoadingImage] = useState(true);
    const [imageError, setImageError] = useState(false);
    const hasChildren = node.children && node.children.length > 0;
   
    // Defaulting 'active' to true if the field is missing from the API response
    const isActive = node.active !== false;
 
    useEffect(() => {
        const loadImage = async () => {
            if (!node.id) {
                setIsLoadingImage(false);
                setImageError(true);
                return;
            }
            setIsLoadingImage(true);
            setImageError(false);
            try {
                const imageUrl = await fetchProfileImageAPI(node.id);
                if (imageUrl) {
                    setProfileImage(imageUrl);
                } else {
                    setImageError(true);
                }
            } catch (err) {
                console.error("Error in loadImage:", err);
                setImageError(true);
            } finally {
                setIsLoadingImage(false);
            }
        };
 
        loadImage();
    }, [node.id]);
 
    const handleToggle = (e) => {
        e.stopPropagation();
        setIsExpanded(!isExpanded);
    };
 
    const styleIndex = depth % 5;
    const avatarInitial = node.name ? node.name.charAt(0).toUpperCase() : '?';
 
    const renderAvatar = () => {
        if (isLoadingImage) {
            return <div className="node-avatar-wrapper shimmer"></div>;
        }
        if (imageError || !profileImage) {
            return <div className="node-avatar-wrapper">{avatarInitial}</div>;
        }
        return (
            <img
                src={profileImage}
                alt={`${node.name}'s profile`}
                className="node-avatar-image"
                onError={() => setImageError(true)}
            />
        );
    };
 
    return (
        <li className="tree-node">
            {/* --- ADDED: 'inactive' class is applied if user is not active --- */}
            <div className={`node-card style-${styleIndex} ${!isActive ? 'inactive' : ''}`}>
                <div className="node-avatar-wrapper">
                    {renderAvatar()}
                </div>
                <div className="node-details">
                    <h3 className="node-title">
                        {node.name || 'Unnamed User'}
                        {/* --- ADDED: Status dot for active/inactive state --- */}
                        <span
                            className={`status-dot ${isActive ? 'active' : 'inactive'}`}
                            title={isActive ? 'Active' : 'Inactive'}
                        ></span>
                    </h3>
                    <p className="node-designation">{node.designation || 'No Designation'}</p>
                    {node.role && (
                        <span className="node-role-badge">
                            {node.role.replace(/_/g, ' ')}
                        </span>
                    )}
                </div>
            </div>
 
            {hasChildren && (
                <>
                    <button onClick={handleToggle} className="toggle-button" title={isExpanded ? 'Collapse' : 'Expand'}>
                        {isExpanded ? <FaAngleUp /> : <FaAngleDown />}
                    </button>
                    <div className={`children-wrapper ${isExpanded ? 'expanded' : 'collapsed'}`}>
                        <ul className="children-container">
                            {node.children.map((child) => (
                                <TreeNode key={child.id} node={child} depth={depth + 1} />
                            ))}
                        </ul>
                    </div>
                </>
            )}
        </li>
    );
};
 
 
// --- Main Team Component ---
const Team = () => {
    const [treeData, setTreeData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isExporting, setIsExporting] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: 'info' });
    const treeContainerRef = useRef(null);
 
    useEffect(() => {
        const loadTree = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const data = await fetchTreeDataAPI();
                setTreeData(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error("Error loading hierarchy:", err);
                setError(err.message);
                setTreeData([]);
            } finally {
                setIsLoading(false);
            }
        };
        loadTree();
    }, []);
 
    useEffect(() => {
        if (toast.show) {
            const timer = setTimeout(() => {
                setToast({ show: false, message: '', type: 'info' });
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [toast]);
 
    const handleExport = async () => {
        if (!treeContainerRef.current) {
            setToast({ show: true, message: 'Chart element not found.', type: 'error' });
            return;
        }
        if (!treeData.length) {
            setToast({ show: true, message: 'No data to export.', type: 'error' });
            return;
        }
 
        setIsExporting(true);
        setToast({ show: true, message: 'Generating chart image...', type: 'info' });
 
        try {
            const dataUrl = await toPng(treeContainerRef.current, {
                cacheBust: true,
                backgroundColor: '#ffffff'
            });
 
            const base64Image = dataUrl.split(',')[1];
           
            setToast({ show: true, message: 'Creating PDF on the server...', type: 'info' });
            const responseData = await exportToPdfAPI(base64Image);
 
            if (responseData && responseData.body) {
                const base64Pdf = responseData.body;
               
                const byteCharacters = atob(base64Pdf);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const pdfBlob = new Blob([byteArray], { type: 'application/pdf' });
               
                const url = window.URL.createObjectURL(pdfBlob);
                const a = document.createElement('a');
 
                const contentDisposition = responseData.headers?.['Content-Disposition'];
                let filename = 'Organization_Chart.pdf';
                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                    if (filenameMatch && filenameMatch[1]) {
                        filename = filenameMatch[1];
                    }
                }
 
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
 
                setToast({ show: true, message: 'PDF downloaded successfully!', type: 'success' });
            } else {
                throw new Error("Invalid or empty response from the PDF export service.");
            }
 
        } catch (exportError) {
            console.error("PDF Export Error:", exportError);
            setToast({ show: true, message: `Export failed: ${exportError.message}`, type: 'error' });
        } finally {
            setIsExporting(false);
        }
    };
 
    const renderContent = () => {
        if (isLoading) return <TeamSkeleton />;
        if (error) return <div className="team-status-message error">{`Error: ${error}`}</div>;
        if (!treeData.length) return <div className="team-status-message">No hierarchy data found.</div>;
   
        return (
            <div className="team-tree-wrapper" ref={treeContainerRef}>
                <ul className="team-tree">
                    {treeData.map((rootNode) => (
                        <TreeNode key={rootNode.id} node={rootNode} />
                    ))}
                </ul>
            </div>
        );
    };
 
    return (
        <div className="team-page-container">
            {toast.show && (
                <div className={`toast-notification ${toast.type}`}>
                    {toast.message}
                </div>
            )}
           
            <header className="team-header">
                <div className="header-content">
                    <MdAccountTree className="header-icon" />
                    <h1>Organization Structure</h1>
                </div>
                <div className="header-actions">
                    <button className="export-button" onClick={handleExport} disabled={isExporting} title="Export to PDF">
                        {isExporting ? (
                            <div className="spinner"></div>
                        ) : (
                            <>
                                <IoDocumentTextOutline />
                                <span>Export PDF</span>
                            </>
                        )}
                    </button>
                </div>
            </header>
           
            <TransformWrapper
                initialScale={0.8}
                minScale={0.1}
                maxScale={4}
                limitToBounds={false}
                panning={{ velocityDisabled: true }}
                wheel={{ step: 0.1 }}
                doubleClick={{ disabled: true }}
            >
                <ZoomControls />
               
                <TransformComponent wrapperClass="canvas-wrapper" contentClass="canvas-content">
                    {renderContent()}
                </TransformComponent>
            </TransformWrapper>
        </div>
    );
};
 
export default Team;